'use client';
import React, { useState, useMemo } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import classNames from 'classnames';
import { JobDetail } from '~/types/job-detail';

interface JobInfoProps {
  job: JobDetail;
  isMobile: boolean;
}

export const JobInfo = React.memo(function JobInfo(props: JobInfoProps) {
  const { job, isMobile } = props;
  const [isCollapsed, setIsCollapsed] = useState(true);

  // 年収情報をメモ化 - より効率的な処理
  const annualInfo = useMemo(() => {
    const salary = job.employment_info.annual_salary;
    if (!salary) return null;

    const parts = salary.split('万円');
    return {
      amount: parts[0],
      suffix: parts[1] || '',
    };
  }, [job.employment_info.annual_salary]);

  // 資格情報を最適化 - サーバーサイドで処理されたデータを軽量に扱う
  const licenseInfo = useMemo(() => {
    const licenseDetail = job.license_skill_info.license_detail;
    const optionalLicenseDetail =
      job.license_skill_info.optional_license_detail;
    const licenseLineCount = job.license_skill_info.license_line_count || 1;

    if (!licenseDetail && !optionalLicenseDetail) {
      return null;
    }

    // サーバーから受け取った行数を使用してパフォーマンス向上
    const shouldCollapse = licenseLineCount >= 5;

    // 表示用コンテンツを事前計算 - splitを最小限に抑制
    let displayText = licenseDetail || '';
    if (shouldCollapse && isCollapsed && licenseDetail) {
      displayText = licenseDetail.split('\n').slice(0, 4).join('\n');
    }

    return {
      displayText,
      optionalText: optionalLicenseDetail || '',
      shouldCollapse,
      hasRequired: !!licenseDetail?.trim(),
      hasOptional: !!optionalLicenseDetail?.trim(),
    };
  }, [
    job.license_skill_info.license_detail,
    job.license_skill_info.optional_license_detail,
    job.license_skill_info.license_line_count,
    isCollapsed,
  ]);

  return (
    <div className="p-job-info">
      <table className="p-job-info__table">
        <tbody>
          {job.employment_info.annual_salary && (
            <tr className="p-job-info__tr">
              <th className="p-job-info__th u-pt11">
                <p
                  className={classNames(
                    'u-flex -items-center',
                    isMobile ? 'u-gap4' : 'u-gap6',
                  )}
                >
                  <Image
                    src="/nextjs/images/icons/ic_money.svg"
                    width={16}
                    height={16}
                    alt="給与アイコン"
                    className="u-mb2 p-job-info__icon"
                    loading="lazy"
                  />
                  <span className="p-job-info__text">年収</span>
                </p>
              </th>
              <td className="p-job-info__td u-pos-rel">
                <b className="p-job-info__text -lg-red">{annualInfo?.amount}</b>
                <b className="p-job-info__text">万円</b>
                {annualInfo?.suffix}
                {!isMobile && (
                  <Link href="#salary" className="c-more-link" prefetch={false}>
                    詳しく見る
                  </Link>
                )}
              </td>
            </tr>
          )}
          {job.category_info.categories &&
            job.category_info.categories.length > 0 && (
              <tr className="p-job-info__tr">
                <th className="p-job-info__th">
                  <p
                    className={classNames(
                      'u-flex -items-center',
                      isMobile ? 'u-gap4' : 'u-gap6',
                    )}
                  >
                    <Image
                      src="/nextjs/images/icons/ic_bag.svg"
                      width={16}
                      height={16}
                      alt="カバンアイコン"
                      className="u-mb2 p-job-info__icon"
                      loading="lazy"
                    />
                    <span className="p-job-info__text">職種</span>
                  </p>
                </th>
                <td className="p-job-info__td u-pos-rel">
                  {job.category_info.categories.map((category, i) => (
                    <span key={i}>
                      {i === 1 || i === 3 ? '/' : i === 2 ? <br /> : ''}
                      {category?.name}
                    </span>
                  ))}
                  {!isMobile && (
                    <Link
                      href="#description"
                      className="c-more-link"
                      prefetch={false}
                    >
                      詳しく見る
                    </Link>
                  )}
                </td>
              </tr>
            )}
          {job.location_info.prefecture && (
            <tr className="p-job-info__tr">
              <th className="p-job-info__th">
                <p
                  className={classNames(
                    'u-flex -items-center',
                    isMobile ? 'u-gap4' : 'u-gap6',
                  )}
                >
                  <Image
                    src="/nextjs/images/icons/ic_location.svg"
                    width={16}
                    height={16}
                    alt="マップアイコン"
                    className="u-mb2 p-job-info__icon"
                    loading="lazy"
                  />
                  <span className="p-job-info__text">勤務地</span>
                </p>
              </th>
              <td className="p-job-info__td u-pos-rel">
                {job.location_info.prefecture?.name}
                {!isMobile && (
                  <Link
                    href="#work_location"
                    className="c-more-link"
                    prefetch={false}
                  >
                    詳しく見る
                  </Link>
                )}
              </td>
            </tr>
          )}
          {(job.license_skill_info.license_detail ||
            job.license_skill_info.optional_license_detail) && (
            <tr className="p-job-info__tr">
              <th className="p-job-info__th">
                <p
                  className={classNames(
                    'u-flex -items-center',
                    isMobile ? 'u-gap4' : 'u-gap6',
                  )}
                >
                  <Image
                    src="/nextjs/images/icons/ic_license.svg"
                    width={16}
                    height={16}
                    alt="資格アイコン"
                    className="u-mb2 p-job-info__icon"
                    loading="lazy"
                  />
                  <span className="p-job-info__text">資格</span>
                </p>
              </th>
              <td className="p-job-info__td u-pos-rel">
                {licenseInfo && (
                  <>
                    <div className="p-job-info__license-content">
                      {licenseInfo.displayText}
                    </div>

                    {licenseInfo.shouldCollapse && (
                      <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className="p-job-info__license-toggle-btn"
                      >
                        {isCollapsed ? 'もっと見る' : '閉じる'}
                      </button>
                    )}

                    {!licenseInfo.hasRequired && licenseInfo.hasOptional && (
                      <div className="p-job-info__optional-license">
                        優遇資格{'\n'}
                        {licenseInfo.optionalText}
                      </div>
                    )}
                  </>
                )}
                {!isMobile && (
                  <Link
                    href="#requirements"
                    className="c-more-link"
                    prefetch={false}
                  >
                    詳しく見る
                  </Link>
                )}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
});
